import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  type FilterOptionsState,
  styled,
  TextField,
} from "@mui/material";
import { useDebounce } from "hooks/debounceHooks";
import { useGeopuntLocation } from "hooks/locationHooks";
import { type SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import type { GeopuntLocation } from "types/types";

interface Props {
  onSelectLocation: (location: GeopuntLocation) => void;
}

export function SearchAutocomplete(props: Props) {
  const { onSelectLocation } = props;
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 500).trim();

  const { data: locations, isLoading } = useGeopuntLocation(debouncedQuery, {
    enabled: debouncedQuery.length > 2,
  });

  return (
    <StyledContainer>
      <Autocomplete
        options={locations || []}
        filterOptions={(
          options: GeopuntLocation[],
          params: FilterOptionsState<GeopuntLocation>
        ) => {
          const arrayOfSplitString: string[] = params.inputValue
            .trim()
            .split(/\W+/);
          const filtered = options.filter((option) => {
            return arrayOfSplitString.some((word) => {
              return option.FormattedAddress.toLowerCase().includes(
                word.toLocaleLowerCase()
              );
            });
          });
          return filtered;
        }}
        onChange={(_, value: GeopuntLocation | null) => {
          if (value) {
            onSelectLocation(value);
          }
        }}
        loading={isLoading}
        getOptionLabel={(location: GeopuntLocation) => {
          return location.FormattedAddress || "";
        }}
        isOptionEqualToValue={(
          option: GeopuntLocation,
          value: GeopuntLocation
        ) => {
          return option.FormattedAddress === value.FormattedAddress;
        }}
        noOptionsText={t("label.noResultsFound")}
        onInputChange={(
          event: SyntheticEvent<Element, Event>,
          newInputValue: string
        ) => {
          setQuery(newInputValue);
        }}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={t("label.typToSearch")}
          />
        )}
      />
    </StyledContainer>
  );
}

const StyledContainer = styled(Box)`
  height: ${({ theme }) => theme.spacing(5)};
  display: flex;
  align-items: center;
  width: 400px;
  background-color: ${({ theme }) => theme.palette.common.white};
  box-shadow: ${({ theme }) => theme.shadows[3]};

  .MuiOutlinedInput-notchedOutline {
    border-style: none;
  }
  .MuiButton-root {
    align-self: stretch;
  }
  .MuiAutocomplete-root {
    flex-grow: 1;
  }

  /* .MuiAutocomplete-option {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(2)};
    border-bottom: 1px ${({ theme }) => theme.palette.grey[100]} solid;
  } */
  .align-right {
    margin-left: auto;
    flex-shrink: 0;
    flex-wrap: nowrap;
  }

  .load-more-button {
    justify-content: center;
  }
`;
